import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Building2, Bug, ChevronLeft, SlidersHorizontal, Scale, Gavel, Users, Eye, UserRoundSearch, Puzzle, Waypoints, Search, Gem, Wrench, Shapes, ScanLine } from 'lucide-react';
import '../components/Campaigns/Campaigns.css';
import useCampaigns from '../hooks/useCampaigns';
import useCampaignSection from '../hooks/useCampaignSection';
import ExecutiveView from '../components/Campaigns/ExecutiveView';
import FilterSelect from '../components/Campaigns/FilterSelect';
import OverviewTab from '../components/Campaigns/OverviewTab';
import ConfirmedBugsFixStatus from '../components/Campaigns/ConfirmedBugsFixStatus';
import CampaignPerformance from '../components/Campaigns/CampaignPerformance';
import IssuesTab from '../components/Campaigns/IssuesTab';
import { useCurrentUser } from '../context/CurrentUserContext';
import { NONE_LABELS, NONE_SENTINEL } from '../components/Campaigns/filterConstants';

const FILTER_GROUPS = [
  {
    label: <><Bug size={16} style={{ marginRight: 4, verticalAlign: 'middle' }} /> Issue</>,
    filters: [
      { key: 'issueType', label: 'Issue Type', labelNode: <><Shapes size={16} style={{ marginRight: 4, verticalAlign: 'middle' }} />Issue Type</> },
      { key: 'severity', label: 'Severity', labelNode: <><Scale size={16} style={{ marginRight: 4, verticalAlign: 'middle' }} />Severity</> },
      { key: 'status', label: 'Status', labelNode: <><Waypoints size={16} style={{ marginRight: 4, verticalAlign: 'middle' }} />Status</> },
      { key: 'resolution', label: 'Resolution', labelNode: <><Gavel size={16} style={{ marginRight: 4, verticalAlign: 'middle' }} />Resolution</> },
      { key: 'inScope', label: 'In scope', labelNode: <><ScanLine size={16} style={{ marginRight: 4, verticalAlign: 'middle' }} />In scope</> },
    ],
  },
  {
    label: <><Building2 size={16} style={{ marginRight: 4, verticalAlign: 'middle' }} /> Organisation</>,
    filters: [
      { key: 'productComponent', label: 'Product Component', labelNode: <><Puzzle size={16} style={{ marginRight: 4, verticalAlign: 'middle' }} />Product Component</> },
      { key: 'productUnit', label: 'Product Unit', labelNode: <><Users size={16} style={{ marginRight: 4, verticalAlign: 'middle' }} />Product Unit</> },
      { key: 'csqPlans', label: 'CSQ Plan', labelNode: <><Gem size={16} style={{ marginRight: 4, verticalAlign: 'middle' }} />CSQ Plan</> },
    ],
  },
  {
    label: <><Users size={16} style={{ marginRight: 4, verticalAlign: 'middle' }} /> People</>,
    filters: [
      { key: 'reporter', label: 'Reporter', labelNode: <><UserRoundSearch size={16} style={{ marginRight: 4, verticalAlign: 'middle' }} />Reporter</> },
      { key: 'assigneeReviewer', label: 'Assignee / Reviewer', labelNode: <><Eye size={16} style={{ marginRight: 4, verticalAlign: 'middle' }} />Assignee / Reviewer</> },
      { key: 'resolvedBy', label: 'Resolved By', labelNode: <><Wrench size={16} style={{ marginRight: 4, verticalAlign: 'middle' }} />Resolved By</> },
    ],
  },
  {
    label: <><Eye size={16} style={{ marginRight: 4, verticalAlign: 'middle' }} /> Review</>,
    filters: [
      { key: 'campaignReviewStatus', label: 'Campaign Review Status', labelNode: <><Search size={16} style={{ marginRight: 4, verticalAlign: 'middle' }} />Campaign Review Status</> },
    ],
  },
];

const FILTERS = FILTER_GROUPS.flatMap(g => g.filters);

const EMPTY_FILTERS = Object.fromEntries(FILTERS.map(f => [f.key, []]));

const SEVERITY_LABEL_MAP = {
  formatLabel: (v) => {
    const lower = v.toLowerCase();
    if (lower.includes('critical')) return '🚨 Critical and Blocker';
    if (lower.includes('major')) return '🔥 Major';
    if (lower.includes('minor')) return '🙁 Minor';
    return v;
  },
};

const TABS = [
  { key: 'overview', label: 'Overview' },
  { key: 'executive', label: 'Severity Details' },
  { key: 'issues', label: 'Issues Details' },
  { key: 'fixstatus', label: 'Fix Status' },
  { key: 'performance', label: 'Campaign Performance' },
];

function formatDate(dateStr) {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString('en-GB', {
    day: 'numeric', month: 'long', year: 'numeric',
  });
}

function Campaigns() {
  const { campaignId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedId = campaignId ? Number(campaignId) : null;

  const [activeTab, setActiveTab] = useState(() => searchParams.get('tab') || 'overview');
  const [filters, setFilters] = useState(() => {
    const initial = { ...EMPTY_FILTERS };
    FILTERS.forEach(f => { const v = searchParams.getAll(f.key); if (v.length) initial[f.key] = v; });
    return initial;
  });

  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [execApplyFilters, setExecApplyFilters] = useState(() => searchParams.get('execFiltered') === '1');
  const [confirmedOnly, setConfirmedOnly] = useState(() => searchParams.get('confirmedOnly') === '1');
  const currentUser = useCurrentUser();
  const jiraName = currentUser?.jiraName ?? null;
  const isQualityTeamMember = currentUser?.isQualityTeamMember === true;
  const [rejectedBy, setRejectedBy] = useState('pu');

  const handleFiltersChange = useCallback((v) => setFilters(v), []);

  // Initialised to the current campaignId so the reset is skipped on mount
  // (URL params are already applied via useState initialisers above).
  // Only resets when the user actually switches to a different campaign.
  const prevCampaignIdRef = useRef(campaignId);

  const { campaigns } = useCampaigns();
  const campaign = campaigns.find(c => c.id === selectedId) ?? null;

  // Filter options — cross-filtered counts (all other active filters applied)
  const activeFilters = confirmedOnly ? { ...filters, confirmedOnly: '1' } : filters;

  const { data: filterOptions, loading: filterOptionsLoading } = useCampaignSection(selectedId, 'filter-options', activeFilters);

  // Section data — filters forwarded as query params
  const { data: executiveData } = useCampaignSection(selectedId, 'executive', execApplyFilters ? activeFilters : EMPTY_FILTERS);
  const { data: severityData } = useCampaignSection(selectedId, 'severity', activeFilters);
  const { data: fixstatusData } = useCampaignSection(selectedId, 'fixstatus', activeFilters);
  const { data: issuesData } = useCampaignSection(selectedId, 'issues', activeFilters);
  // "Confirmed bugs only" would always empty this list, so it is left out (and out of the refetch key).
  const { data: rejectedData } = useCampaignSection(selectedId, 'rejected', { ...filters, rejectedBy });
  const { data: performanceData } = useCampaignSection(selectedId, 'performance', EMPTY_FILTERS);
  const { data: reviewStatusData } = useCampaignSection(selectedId, 'review-status', { ...activeFilters, campaignReviewStatus: [] });

  const visibleTabs = (() => {
    const sections = campaign?.config?.sections;
    if (!sections) return TABS;
    const mapped = sections.map(s => TABS.find(t => t.label === s.name)).filter(Boolean);
    // Overview is always first, Issues Details always second
    const overview = TABS.find(t => t.key === 'overview');
    const issues = TABS.find(t => t.key === 'issues');
    const rest = mapped.filter(t => t.key !== 'overview' && t.key !== 'issues');
    return [overview, issues, ...rest];
  })();

  // Sync active filters + tab + exec toggle → URL query params
  // (search params q/qd are managed by IssuesTab directly)
  useEffect(() => {
    const params = new URLSearchParams();
    FILTERS.forEach(f => { filters[f.key].forEach(v => params.append(f.key, v)); });
    if (activeTab && activeTab !== 'overview') params.set('tab', activeTab);
    if (execApplyFilters) params.set('execFiltered', '1');
    if (confirmedOnly) params.set('confirmedOnly', '1');
    // Preserve q/qd if present — owned by IssuesTab
    const current = new URLSearchParams(window.location.search);
    if (current.get('q')) params.set('q', current.get('q'));
    if (current.get('qd')) params.set('qd', current.get('qd'));
    setSearchParams(params, { replace: true });
  }, [filters, activeTab, execApplyFilters, confirmedOnly]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset tab, filters, and exec toggle when navigating to a different campaign.
  // Skipped on initial mount because state is already seeded from URL params above.
  useEffect(() => {
    if (prevCampaignIdRef.current === campaignId) return;
    prevCampaignIdRef.current = campaignId;
    setFilters(EMPTY_FILTERS);
    setActiveTab('overview');
    setExecApplyFilters(false);
    setConfirmedOnly(false);
  }, [campaignId]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="application-container campaign-page">

      {/* Full-width campaign header */}
      <div className="campaign-header">
        <div className="campaign-header-center">
          {campaign ? (
            <>
              <h1>{campaign.campaignName}</h1>
              {campaign.startDate && (
                <p className="campaign-dates">
                  {formatDate(campaign.startDate)}
                  {campaign.endDate && ` – ${formatDate(campaign.endDate)}`}
                </p>
              )}
            </>
          ) : (
            <h1>Test Campaigns</h1>
          )}
        </div>

        <div className="campaign-header-right">
          <select
            className="campaign-selector"
            value={selectedId ?? ''}
            onChange={e => e.target.value ? navigate(`/campaigns/${e.target.value}`) : navigate('/campaigns')}
          >
            <option value="">Select a campaign…</option>
            {campaigns.map(c => (
              <option key={c.id} value={c.id}>{c.campaignName}</option>
            ))}
          </select>
        </div>
      </div>

      {campaign ? (
        <div className="campaign-body">

          {/* Filter sidebar */}
          <div className={`campaign-sidebar-container right${sidebarCollapsed ? ' collapsed' : ''}`}>
            <button
              className={`sidebar-toggle-button right${sidebarCollapsed ? ' collapsed' : ''}`}
              onClick={() => setSidebarCollapsed(c => !c)}
              aria-label="Toggle filters"
            >
              <ChevronLeft size={20} />
            </button>
            <div className="campaign-sidebar">
              <span className="campaign-sidebar-collapsed-label"><SlidersHorizontal size={20} /></span>
              <div className="sidebar-filters-content">
                <div className="sidebar-title"><SlidersHorizontal size={16} style={{ marginRight: 5, verticalAlign: 'middle', flexShrink: 0 }} />Filters</div>
                <label className="confirmed-bugs-toggle">
                  <input
                    type="checkbox"
                    checked={confirmedOnly}
                    onChange={e => setConfirmedOnly(e.target.checked)}
                  />
                  Confirmed bugs only
                </label>
                {FILTER_GROUPS.map(group => (
                  <div key={group.label} className="filter-group">
                    <div className="filter-group-label">{group.label}</div>
                    {group.filters.map(f => {
                      const options = filterOptions?.[f.key] ?? [];
                      return (
                        <div key={f.key} className="filter-item">
                          <label>{f.labelNode ?? f.label}</label>
                          <FilterSelect
                            options={options}
                            value={filters[f.key]}
                            disabled={filterOptionsLoading}
                            onChange={v => setFilters(prev => ({ ...prev, [f.key]: v }))}
                            formatLabel={
                              f.key === 'severity' ? SEVERITY_LABEL_MAP.formatLabel
                              : NONE_LABELS[f.key] ? (v => v === NONE_SENTINEL ? NONE_LABELS[f.key] : v)
                              : undefined
                            }
                          />
                        </div>
                      );
                    })}
                  </div>
                ))}
                <div style={{ height: 40 }} />
              </div>
            </div>
          </div>

          {/* Main content */}
          <div className="campaign-main">

            <div className="campaign-tabs">
              {visibleTabs.map(t => (
                <button
                  key={t.key}
                  className={`tab-btn${activeTab === t.key ? ' active' : ''}`}
                  onClick={() => setActiveTab(t.key)}
                >
                  {t.label}
                </button>
              ))}
            </div>

            <div className="tab-panel">
              {activeTab === 'executive' && (
                severityData
                  ? <ExecutiveView severityData={severityData} />
                  : <div className="section-loading">Loading…</div>
              )}
              {activeTab === 'overview' && (
                <>
                  <div className="tab-panel-topbar">
                    <label className="tab-apply-filters-toggle">
                      <input
                        type="checkbox"
                        checked={execApplyFilters}
                        onChange={() => setExecApplyFilters(v => !v)}
                      />
                      Apply filters
                    </label>
                  </div>
                  <OverviewTab campaign={campaign} executiveData={executiveData} />
                </>
              )}
              {activeTab === 'issues' && (
                <IssuesTab
                  campaignId={selectedId}
                  filters={activeFilters}
                  jiraName={jiraName}
                  emptyFilters={EMPTY_FILTERS}
                  onFiltersChange={handleFiltersChange}
                />
              )}
              {activeTab === 'fixstatus' && (
                fixstatusData
                  ? <ConfirmedBugsFixStatus
                    fixProgress={fixstatusData.fixProgress}
                    fixDone={fixstatusData.fixDone}
                    openedBySeverity={fixstatusData.openedBySeverity}
                    fixByUnit={fixstatusData.fixByUnit}
                    trendData={fixstatusData.trendData}
                    quickFixIssues={(issuesData ?? []).filter(i => i.labels?.toLowerCase().includes('quick-fix'))}
                    isQualityTeamMember={isQualityTeamMember}
                    rejectedIssues={rejectedData}
                    rejectedBy={rejectedBy}
                    onRejectedByChange={setRejectedBy}
                  />
                  : <div className="section-loading">Loading…</div>
              )}
              {activeTab === 'performance' && (
                performanceData
                  ? <CampaignPerformance
                    topChampions={performanceData.topChampions}
                    reviewStatus={reviewStatusData ?? performanceData.reviewStatus}
                    reviewsPerQE={performanceData.reviewsPerQE}
                    noHeader
                    onGoToReviewStatus={(status) => {
                      setFilters(prev => ({ ...prev, campaignReviewStatus: [status] }));
                      setActiveTab('issues');
                    }}
                  />
                  : <div className="section-loading">Loading…</div>
              )}
            </div>

          </div>
        </div>
      ) : (
        <div className="campaign-select-screen">
          <p style={{ color: 'var(--cmp-muted)' }}>Select a campaign from the dropdown above to get started.</p>
        </div>
      )}
    </div>
  );
}

export default Campaigns;
